import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Plane, Download, Copy, Share2, Clock, CheckCircle2,
  XCircle, AlertCircle, ArrowRight, Shield
} from "lucide-react";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useCountdown(expiresIso) {
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    if (!expiresIso) return;
    const update = () => {
      const now = new Date();
      const exp = new Date(expiresIso);
      const diff = exp - now;
      if (diff <= 0) {
        setTimeLeft({ expired: true, days: 0, hours: 0, minutes: 0, seconds: 0, totalMs: 0, percent: 100 });
      } else {
        const days = Math.floor(diff / 86400000);
        const hours = Math.floor((diff % 86400000) / 3600000);
        const minutes = Math.floor((diff % 3600000) / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        setTimeLeft({ expired: false, days, hours, minutes, seconds, totalMs: diff });
      }
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [expiresIso]);

  return timeLeft;
}

function getProgressPercent(createdAt, expiresIso) {
  if (!createdAt || !expiresIso) return 0;
  const start = new Date(createdAt);
  const end = new Date(expiresIso);
  const now = new Date();
  const total = end - start;
  const elapsed = now - start;
  if (total <= 0) return 100;
  return Math.min(100, Math.max(0, (elapsed / total) * 100));
}

function getProgressColor(percent) {
  if (percent >= 90) return "bg-red-500";
  if (percent >= 70) return "bg-yellow-500";
  return "bg-[#22c55e]";
}

const statusConfig = {
  confirmed: { icon: CheckCircle2, color: "text-[#22c55e]", bg: "bg-green-50", border: "border-green-200", label: "CONFIRMED" },
  expired: { icon: XCircle, color: "text-red-500", bg: "bg-red-50", border: "border-red-200", label: "EXPIRED" },
  cancelled: { icon: AlertCircle, color: "text-[#6b7280]", bg: "bg-gray-50", border: "border-gray-200", label: "CANCELLED" },
};

export default function VerifyOrder() {
  const { t } = useTranslation();
  const { bookingRef } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/orders/${bookingRef}`);
        setOrder(res.data);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    if (bookingRef) fetch();
  }, [bookingRef]);

  const countdown = useCountdown(order?.validity_expires_at_iso);
  const progress = useMemo(
    () => getProgressPercent(order?.created_at, order?.validity_expires_at_iso),
    [order?.created_at, order?.validity_expires_at_iso]
  );

  const handleCopyRef = () => {
    navigator.clipboard.writeText(bookingRef);
    toast.success(t("success.copied"));
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: `Flight Reservation ${bookingRef}`, url });
    } else {
      navigator.clipboard.writeText(url);
      toast.success(t("success.link_copied"));
    }
  };

  const handleDownloadPDF = () => {
    window.open(`${API}/orders/${bookingRef}/pdf`, "_blank");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <div className="max-w-2xl w-full mx-auto px-6">
          <Skeleton className="h-16 w-full mb-6 rounded-2xl" />
          <Skeleton className="h-40 w-full mb-6 rounded-2xl" />
          <Skeleton className="h-60 w-full rounded-2xl" />
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center" data-testid="verify-not-found">
        <div className="text-center">
          <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="font-['Outfit'] text-2xl font-bold text-[#1e3a5f] mb-2">{t("verify.not_found_title")}</h1>
          <p className="text-[#6b7280]">{t("verify.not_found_desc")}</p>
        </div>
      </div>
    );
  }

  const fd = order.flight_data || {};
  const status = order.status || "confirmed";
  const isExpired = status === "expired" || countdown?.expired;
  const sc = statusConfig[isExpired ? "expired" : status] || statusConfig.confirmed;
  const StatusIcon = sc.icon;

  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="verify-page">
      {/* Header Bar */}
      <div className="bg-[#1e3a5f] text-white py-4">
        <div className="max-w-3xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#f97316] rounded-lg flex items-center justify-center">
              <Plane className="w-4 h-4 text-white -rotate-45" />
            </div>
            <span className="font-['Outfit'] font-bold text-base">OnwardFlighTickets</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-white/70">
            <Shield className="w-4 h-4" />
            <span>{t("verify.header_title")}</span>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8 md:py-12">
        {/* Status + Countdown Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`${sc.bg} border ${sc.border} rounded-2xl p-6 md:p-8 mb-6`}
          data-testid="status-card"
        >
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <StatusIcon className={`w-6 h-6 ${sc.color}`} />
              <span className={`font-['Outfit'] text-lg font-bold ${sc.color}`} data-testid="order-status">
                {sc.label}
              </span>
            </div>
            <div className="text-right">
              <p className="text-xs text-[#6b7280]">{t("verify.booking_ref")}</p>
              <p className="font-['Outfit'] text-xl font-bold text-[#1e3a5f] tracking-widest" data-testid="verify-booking-ref">
                {bookingRef}
              </p>
            </div>
          </div>

          {/* Countdown */}
          {countdown && !isExpired ? (
            <div data-testid="countdown-section">
              <p className="text-sm text-[#6b7280] mb-2">
                <Clock className="w-4 h-4 inline mr-1" />
                {t("verify.valid_for")}
              </p>
              <div className="flex gap-3 mb-4" data-testid="countdown-display">
                {[
                  { val: countdown.days, label: t("verify.days") },
                  { val: countdown.hours, label: t("verify.hours") },
                  { val: countdown.minutes, label: t("verify.minutes") },
                  { val: countdown.seconds, label: t("verify.seconds") },
                ].map((unit) => (
                  <div key={unit.label} className="bg-white rounded-xl px-4 py-3 text-center border border-[#e2e8f0] min-w-[60px]">
                    <p className="font-['Outfit'] text-2xl font-bold text-[#1e3a5f]">{String(unit.val).padStart(2, "0")}</p>
                    <p className="text-[10px] text-[#6b7280] uppercase tracking-wider">{unit.label}</p>
                  </div>
                ))}
              </div>
              <div className="w-full h-2.5 bg-white rounded-full overflow-hidden border border-[#e2e8f0]" data-testid="progress-bar-validity">
                <div
                  className={`h-full rounded-full transition-all duration-1000 ${getProgressColor(progress)}`}
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-xs text-[#6b7280] mt-2">
                {t("verify.expires")}: {order.validity_expires_at}
              </p>
            </div>
          ) : (
            <div className="text-center py-4" data-testid="expired-notice">
              <p className="text-red-600 font-medium">{t("verify.expired_message")}</p>
              <p className="text-xs text-[#6b7280] mt-1">{t("verify.expired_on")}: {order.validity_expires_at}</p>
            </div>
          )}
        </motion.div>

        {/* Traveler Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white border border-[#e2e8f0] rounded-2xl p-6 mb-6"
          data-testid="traveler-card"
        >
          <h3 className="text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-3">{t("verify.traveler")}</h3>
          {order.passengers?.length > 0 ? (
            <div className="space-y-1">
              {order.passengers.map((p, i) => (
                <p key={i} className={`font-['Outfit'] ${i === 0 ? "text-lg font-semibold" : "text-sm"} text-[#1e3a5f]`}>
                  {p.title} {p.first_name} {p.last_name}
                </p>
              ))}
            </div>
          ) : (
            <p className="font-['Outfit'] text-lg font-semibold text-[#1e3a5f]">
              {order.passenger_title} {order.passenger_first_name} {order.passenger_last_name}
            </p>
          )}
          <p className="text-sm text-[#6b7280] mt-1">
            {t("verify.booking_ref")}: {bookingRef}
          </p>
        </motion.div>

        {/* Flight Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white border border-[#e2e8f0] rounded-2xl p-6 mb-6"
          data-testid="flight-details-card"
        >
          <h3 className="text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-4">{t("verify.flight_details")}</h3>

          {/* Airline */}
          <div className="flex items-center gap-3 mb-5">
            {fd.airline_logo && <img src={fd.airline_logo} alt={fd.airline} className="w-8 h-8 object-contain" />}
            <div>
              <p className="text-sm font-medium text-[#1f2937]">{fd.airline}</p>
              <p className="text-xs text-[#6b7280]">{fd.flight_number} &middot; {fd.travel_class || "Economy"}</p>
            </div>
          </div>

          {/* Route */}
          <div className="flex items-center gap-4 mb-5">
            <div className="text-center flex-1">
              <p className="font-['Outfit'] text-3xl font-bold text-[#1e3a5f]">{fd.departure_code}</p>
              <p className="text-sm text-[#1f2937]">{fd.departure_airport?.substring(0, 30)}</p>
              <p className="text-lg font-semibold text-[#1e3a5f] mt-2">{fd.departure_time}</p>
              <p className="text-xs text-[#6b7280]">{fd.departure_date}</p>
            </div>

            <div className="flex flex-col items-center min-w-[100px]">
              <p className="text-xs text-[#6b7280] mb-1">{fd.stops_detail || "Nonstop"}</p>
              <div className="w-full flex items-center">
                <div className="w-2 h-2 rounded-full border-2 border-[#1e3a5f]" />
                <div className="flex-1 h-px bg-[#d1d5db]" />
                <Plane className="w-4 h-4 text-[#f97316] -rotate-45" />
              </div>
              <p className="text-xs text-[#6b7280] mt-1">{fd.duration_text}</p>
            </div>

            <div className="text-center flex-1">
              <p className="font-['Outfit'] text-3xl font-bold text-[#1e3a5f]">{fd.arrival_code}</p>
              <p className="text-sm text-[#1f2937]">{fd.arrival_airport?.substring(0, 30)}</p>
              <p className="text-lg font-semibold text-[#1e3a5f] mt-2">{fd.arrival_time}</p>
              <p className="text-xs text-[#6b7280]">{fd.arrival_date}</p>
            </div>
          </div>

          {/* Multi-leg details */}
          {fd.legs && fd.legs.length > 1 && (
            <div className="border-t border-[#f1f5f9] pt-4 mt-4">
              <p className="text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-3">{t("verify.flight_segments")}</p>
              {fd.legs.map((leg, i) => (
                <div key={i}>
                  {i > 0 && fd.layovers?.[i - 1] && (
                    <div className="flex items-center gap-2 my-2 ml-4">
                      <Clock className="w-3 h-3 text-[#f97316]" />
                      <span className="text-xs text-[#6b7280]">
                        Layover: {fd.layovers[i - 1].duration} in {fd.layovers[i - 1].airport}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-[#1f2937]">
                    <Plane className="w-3 h-3 text-[#6b7280]" />
                    <span>{leg.departure_code} {leg.departure_time}</span>
                    <ArrowRight className="w-3 h-3 text-[#6b7280]" />
                    <span>{leg.arrival_code} {leg.arrival_time}</span>
                    <span className="text-[#6b7280]">&middot; {leg.airline} {leg.flight_number}</span>
                    <span className="text-[#6b7280]">&middot; {leg.duration_text}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Return Flight */}
          {order.return_flight_data && (() => {
            const rfd = order.return_flight_data;
            return (
              <div className="border-t border-[#e2e8f0] pt-5 mt-5">
                <p className="text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-4">Return Flight</p>
                <div className="flex items-center gap-3 mb-4">
                  {rfd.airline_logo && <img src={rfd.airline_logo} alt={rfd.airline} className="w-8 h-8 object-contain" />}
                  <div>
                    <p className="text-sm font-medium text-[#1f2937]">{rfd.airline}</p>
                    <p className="text-xs text-[#6b7280]">{rfd.flight_number}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-center flex-1">
                    <p className="font-['Outfit'] text-3xl font-bold text-[#1e3a5f]">{rfd.departure_code}</p>
                    <p className="text-lg font-semibold text-[#1e3a5f] mt-1">{rfd.departure_time}</p>
                    <p className="text-xs text-[#6b7280]">{rfd.departure_date}</p>
                  </div>
                  <div className="flex flex-col items-center min-w-[80px]">
                    <div className="w-full flex items-center">
                      <div className="w-2 h-2 rounded-full border-2 border-[#1e3a5f]" />
                      <div className="flex-1 h-px bg-[#d1d5db]" />
                      <Plane className="w-4 h-4 text-[#f97316] -rotate-45" />
                    </div>
                    <p className="text-xs text-[#6b7280] mt-1">{rfd.duration_text}</p>
                  </div>
                  <div className="text-center flex-1">
                    <p className="font-['Outfit'] text-3xl font-bold text-[#1e3a5f]">{rfd.arrival_code}</p>
                    <p className="text-lg font-semibold text-[#1e3a5f] mt-1">{rfd.arrival_time}</p>
                    <p className="text-xs text-[#6b7280]">{rfd.arrival_date || rfd.departure_date}</p>
                  </div>
                </div>
              </div>
            );
          })()}
        </motion.div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8"
        >
          <button
            onClick={handleDownloadPDF}
            className="flex items-center justify-center gap-2 bg-[#f97316] text-white py-3 rounded-xl font-medium hover:bg-[#ea580c] transition-all hover:-translate-y-0.5 hover:shadow-lg"
            data-testid="verify-download-pdf"
          >
            <Download className="w-4 h-4" /> {t("success.download_pdf")}
          </button>
          <button
            onClick={handleCopyRef}
            className="flex items-center justify-center gap-2 bg-[#1e3a5f] text-white py-3 rounded-xl font-medium hover:bg-[#152e4d] transition-all hover:-translate-y-0.5 hover:shadow-lg"
            data-testid="verify-copy-ref"
          >
            <Copy className="w-4 h-4" /> {t("success.copy_ref")}
          </button>
          <button
            onClick={handleShare}
            className="flex items-center justify-center gap-2 bg-white border border-[#e2e8f0] text-[#1f2937] py-3 rounded-xl font-medium hover:bg-[#f8fafc] transition-all"
            data-testid="verify-share"
          >
            <Share2 className="w-4 h-4" /> {t("verify.share")}
          </button>
        </motion.div>

        {/* Footer */}
        <div className="border-t border-[#e2e8f0] pt-6 text-center text-xs text-[#6b7280]">
          <p>{t("verify.footer_generated")}</p>
          <p className="mt-1">{t("verify.footer_support")}: support@onwardflightickets.com</p>
          <p className="mt-1">&copy; 2026 OnwardFlighTickets</p>
        </div>
      </div>
    </div>
  );
}
